import React, { useRef, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Modal from "@mui/material/Modal";

import { getFormFieldOrder, sortByFieldOrder } from "../../Forms/formFieldOrder";
import { fontFamily, useSharedValue } from "../Constants/constants";
import backArrowIcon from "../../assets/new icons/extraction review/back arrow.svg";
import rightArrowIcon from "../../assets/new icons/extraction review/right arrow.svg";
import tickCircleIcon from "../../assets/new icons/extraction review/tick-circle.svg";
import SecureDocumentViewer from "../WorkLoade_File_Import/SecureDocumentViewer";
import FormComparisonModal from "./FormComparisonModal";
import FormTablesDisplay from "./FormTablesDisplay";
import "../../Forms/style.css";
import "../Constants/config.css";

const formatFieldNameForDisplay = (fieldName) => {
  if (!fieldName) return "";
  const formattedFieldName = fieldName
    .replace(/[_-]/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return formattedFieldName
    .replace(/\bW\s*9\b/gi, "W-9")
    .replace(/\bW\s*8\s*BEN\s*E\b/gi, "W-8BEN-E")
    .replace(/\bW\s*8\s*BENE\b/gi, "W-8BEN-E")
    .replace(/\bW\s*8\s*BEN\b/gi, "W-8BEN")
    .replace(/\bW\s*8\s*IMY\b/gi, "W-8IMY")
    .replace(/\bW\s*8\s*ECI\b/gi, "W-8ECI")
    .replace(/\bW\s*8\s*EXP\b/gi, "W-8EXP")
    .replace(/\bGIIN\b/gi, "GIIN")
    .replace(/\bEIN\b/gi, "EIN")
    .replace(/\bTIN\b/gi, "TIN")
    .replace(/\bUS\b/gi, "U.S.");
};

const getStandardizedFieldName = (fieldName) => {
  const fieldMapping = {
    "IMY-Name_of_organization": "Name of organization",
    "W8-BENE-Organization_name": "Name of organization",
    "EXP-Organization_name": "Name of organization",
    Organization_name: "Name of organization",
    organizationName: "Name of organization",
    organization_name: "Name of organization",

    "W8BEN-Beneficial_owner_name": "Beneficial Owner name",
    Beneficial_owner: "Beneficial Owner name",
    beneficial_owner: "Beneficial Owner name",
    beneficialOwner: "Beneficial Owner name",

    "W8BEN-US_taxpayer_id": "U.S. taxpayer identification number",
    US_taxpayer_identification_number: "U.S. taxpayer identification number",
    US_Taxpayer_identification_number: "U.S. taxpayer identification number",
    "W8_BENE-8-us-taxpayer-id": "U.S. taxpayer identification number",
    us_taxpayer_id: "U.S. taxpayer identification number",

    "Country_of_incorporation/organization": "Country of incorporation or organization",
    "W8_BENE-Country_of_incorporation_organization": "Country of incorporation or organization",
    Country_of_incorporation_organization: "Country of incorporation or organization",

    "W8BEN-Country_of_citizenship": "Country of citizenship",
    Country_of_citizenship: "Country of citizenship",
    country_of_citizenship: "Country of citizenship",

    Foreign_tax_identifying_number: "Foreign tax identifying number",
    "W8_BENE-Foreign_tax_identifying_number": "Foreign tax identifying number",
    "W8BEN-Foreign_taxpayer_id": "Foreign tax identifying number",
    foreign_tax_id: "Foreign tax identifying number",

    "W8BEN-Permanent_address_street": "Permanent residence address",
    Permanent_address_street: "Permanent residence address",
    "W8_BENE-Permanent_address_street": "Permanent residence address",
    "W9-Address": "Permanent residence address",
    "ECI-Business_address_street": "Permanent residence address",
    permanent_address_street: "Permanent residence address",

    "W8BEN-Permanent_address_city_state_postal": "Permanent city address",
    Permanent_address_city_state_postal: "Permanent city address",
    "W8_BENE-Permanent_address_city_state_postal": "Permanent city address",
    "W9-City_state_zip": "Permanent city address",
    "ECI-Business_address_city_state_zip": "Permanent city address",
    permanent_address_city: "Permanent city address",

    "W8BEN-Permanent_address_country": "Permanent address country",
    Permanent_address_country: "Permanent address country",
    "W8_BENE-Permanent_address_country": "Permanent address country",
    permanent_address_country: "Permanent address country",

    "W8BEN-Mailing_address_street": "Mailing address",
    Mailing_address_street: "Mailing address",
    "W8_BENE-Mailing_address_street": "Mailing address",
    mailing_address_street: "Mailing address",

    Mailing_address_city_state_postal: "Mailing address city",
    "W8_BENE-Mailing_address_city_state_postal": "Mailing address city",
    mailing_address_city: "Mailing address city",

    Mailing_address_country: "Mailing address country",
    "W8_BENE-Mailing_address_country": "Mailing address country",
    mailing_address_country: "Mailing address country",

    "EXP-GIIN": "GIIN",
    "W8_BENE-9A_giin": "GIIN",
    "W8_BENE-13_giin": "GIIN",
    giin: "GIIN",

    "W8BEN-Date_of_birth": "Date of birth",
    Date_of_birth: "Date of birth",
    date_of_birth: "Date of birth",
    dateOfBirth: "Date of birth",

    "W9-Social_security_number": "Social security number",
    Social_security_number: "Social security number",
    social_security_number: "Social security number",
    ssn: "Social security number",

    "W9-Employer_identification_number": "Employer identification number",
    Employer_identification_number: "Employer identification number",
    employer_identification_number: "Employer identification number",
    ein: "Employer identification number",

    uniqueidentifier: "Unique Identifier",
    uniqueIdentifier: "Unique Identifier",
    unique_identifier: "Unique Identifier",
  };

  return fieldMapping[fieldName] || formatFieldNameForDisplay(fieldName);
};

/**
 * SingleFormPopup
 *
 * Props:
 *   open                    {boolean}
 *   onClose                 {function}
 *   singleFormData          {object|null}  – { row, formType, extractedData, ... }
 *   singleFormViewMode      {string}       – "form" | "keyValue"
 *   setSingleFormViewMode   {function}
 *   singleFormMatchingData  {object|null}  – { matchCount, entities }
 *   isSingleFormSearchLoading {boolean}
 *   selectedCheckbox        {string|null}  – currently selected fileImportId
 *   forceFormRerender       {number}
 *   getSingleFormComponent  {function}     – (formType, extractedData, row, forceKey) => ReactNode
 *   handleAssignMatchingIdentifier {function}
 *   onOpenComparison        {function}     – (formId, fileImportId) => void
 *   isFormComparisonOpen    {boolean}
 *   onCloseComparison       {function}
 *   currentPdfFileImportId  {string}
 *   selectedFormIdForComparison {string}
 *   setSelectedFormIdForComparison {function}
 *   setCurrentPdfFileImportId {function}
 *   setIsFormComparisonOpen  {function}
 */
function SingleFormPopup({
  open,
  onClose,
  singleFormData,
  singleFormViewMode,
  setSingleFormViewMode,
  singleFormMatchingData,
  isSingleFormSearchLoading,
  selectedCheckbox,
  forceFormRerender,
  getSingleFormComponent,
  handleAssignMatchingIdentifier,
  isFormComparisonOpen,
  onCloseComparison,
  currentPdfFileImportId,
  selectedFormIdForComparison,
  setSelectedFormIdForComparison,
  setCurrentPdfFileImportId,
  setIsFormComparisonOpen,
}) {
  const { isMenuOpen } = useSharedValue();
  const formContainerRef = useRef(null);
  const [isSavingForm, setIsSavingForm] = useState(false);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="single-form-modal-title"
        aria-describedby="single-form-modal-description"
        style={{
          border: "none",
          background: "transparent",
          boxShadow: "none",
        }}
      >
        <div
          style={{
            right: 20,
            bottom: 10,
            border: "none",
            outline: "none",
            position: "absolute",
            width: isMenuOpen === true ? "calc(100vw - 280px)" : "calc(100vw - 110px)",
            minHeight: "calc(100vh - 95px)",
            maxHeight: "calc(100vh - 95px)",
            padding: "20px",
            backgroundColor: "white",
            boxShadow: "none",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header – 3-column so badge+toggle stay centred regardless of name length */}
          <div
            style={{
              padding: "12px 20px 12px 0",
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
            }}
          >
            {/* Left: back arrow + file name */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                flex: 1,
                minWidth: 0,
              }}
            >
              <img
                src={backArrowIcon}
                alt="Back"
                className="back-arrow-icon"
                style={{ width: "18px", height: "18px", flexShrink: 0, marginTop: 2 }}
                onClick={onClose}
              />
              <h6
                style={{
                  color: "#424242",
                  fontFamily: fontFamily,
                  margin: 0,
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                Name : {singleFormData?.row?.fileName || "Unknown"}
              </h6>
            </div>

            {/* Centre: match badge + view toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: "15px", flexShrink: 0 }}>
              {!isSingleFormSearchLoading && (
                <div className="match-badge" style={{ fontFamily: fontFamily }}>
                  <img
                    src={tickCircleIcon}
                    alt="Match status"
                    style={{ width: "14px", height: "14px", filter: "var(--icon-download-filter)" }}
                  />
                  {singleFormMatchingData && singleFormMatchingData.matchCount !== undefined
                    ? `${singleFormMatchingData.matchCount} Matches Found`
                    : "0 Matches Found"}
                </div>
              )}
              {/* Toggle between Edit Form and Extracted Data */}
              <div className="view-toggle-wrapper">
                {/* Background slider */}
                <div
                  className="view-toggle-slider"
                  style={{ left: singleFormViewMode === "form" ? "4px" : "114px" }}
                />
                <div
                  onClick={() => setSingleFormViewMode("form")}
                  className={`view-toggle-option${singleFormViewMode === "form" ? " view-toggle-option--active" : ""}`}
                >
                  Edit Form
                </div>
                <div
                  onClick={() => setSingleFormViewMode("keyValue")}
                  className={`view-toggle-option${singleFormViewMode === "keyValue" ? " view-toggle-option--active" : ""}`}
                >
                  Extracted Data
                </div>
              </div>
            </div>

            {/* Right: loading indicator (mirrors left flex:1 to keep centre truly centred) */}
            <div
              style={{ flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center" }}
            >
              {isSingleFormSearchLoading && (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <CircularProgress size={16} style={{ color: "var(--loader-color)" }} />
                  <span
                    style={{
                      fontSize: "12px",
                      color: "var(--application-primary-color)",
                      fontFamily: fontFamily,
                    }}
                  >
                    Searching for matching data...
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
            {/* Left side – Edit Form or Extracted Data */}
            <div
              style={{
                width:
                  singleFormMatchingData && singleFormMatchingData.matchCount > 0
                    ? singleFormData && Object.keys(singleFormData).length > 0
                      ? "33.33%"
                      : "50%"
                    : "50%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                borderRight:
                  singleFormMatchingData && singleFormMatchingData.matchCount > 0
                    ? "1px solid #e0e0e0"
                    : "none",
              }}
            >
              {singleFormViewMode === "form" ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    padding: "15px",
                    paddingBottom: 0,
                  }}
                >
                  {/* Scrollable form content */}
                  <div
                    ref={formContainerRef}
                    className="hide-internal-submit"
                    style={{ flex: 1, overflowY: "auto", width: "100%" }}
                  >
                    {singleFormData?.formType && singleFormData?.extractedData ? (
                      getSingleFormComponent(
                        singleFormData.formType,
                        singleFormData.extractedData,
                        singleFormData?.row,
                        forceFormRerender
                      )
                    ) : (
                      <div
                        style={{
                          color: "#666",
                          fontStyle: "italic",
                          fontFamily: fontFamily,
                          fontSize: "12px",
                          textAlign: "center",
                          marginTop: "50px",
                        }}
                      >
                        {!singleFormData?.formType ? (
                          <div>
                            <div>Form type not recognized from filename</div>
                            <div style={{ marginTop: "10px", fontSize: "11px", color: "#999" }}>
                              Filename: {singleFormData?.row?.fileName}
                            </div>
                          </div>
                        ) : (
                          "No form data available for editing"
                        )}
                      </div>
                    )}
                  </div>
                  {/* Always-visible Save Form button */}
                  <div
                    style={{
                      flexShrink: 0,
                      paddingTop: "10px",
                      paddingBottom: "15px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      disabled={isSavingForm}
                      onClick={() => {
                        const submitBtn =
                          formContainerRef.current?.querySelector('[type="submit"]');
                        if (submitBtn) {
                          setIsSavingForm(true);
                          submitBtn.click();
                          setTimeout(() => setIsSavingForm(false), 2500);
                        }
                      }}
                      style={{
                        backgroundColor: "var(--btn-primary-bg)",
                        color: "var(--btn-primary-text)",
                        border: "none",
                        borderRadius: "4px",
                        padding: "6px 20px",
                        cursor: isSavingForm ? "not-allowed" : "pointer",
                        fontFamily: fontFamily,
                        fontSize: "14px",
                        fontWeight: 500,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        opacity: isSavingForm ? 0.8 : 1,
                      }}
                    >
                      {isSavingForm ? (
                        <>
                          <CircularProgress size={13} style={{ color: "white" }} />
                          Saving...
                        </>
                      ) : (
                        <>
                          Save Form
                          <img
                            src={rightArrowIcon}
                            alt=""
                            style={{ width: "12px", height: "12px" }}
                          />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {singleFormData?.extractedData ? (
                    <div style={{ fontFamily: fontFamily, marginTop: "20px", overflowY: "auto" }}>
                      {(() => {
                        let data = singleFormData.extractedData;
                        if (typeof data === "string") {
                          try {
                            data = JSON.parse(data);
                          } catch (e) {
                            data = { data };
                          }
                        }
                        if (Array.isArray(data) && data.length > 0) data = data[0];

                        if (typeof data === "object" && data !== null) {
                          const fieldOrder = getFormFieldOrder(singleFormData?.formType || "");
                          const filteredEntries = Object.entries(data)
                            .filter(
                              ([key]) =>
                                key !== "viewPdf" &&
                                key !== "formType" &&
                                key !== "FormType" &&
                                key !== "Form Type" &&
                                key !== "Form number" &&
                                key !== "Form_number" &&
                                key !== "Entity Type" &&
                                key !== "Chapter4Status" &&
                                key !== "tables" &&
                                key !== "incomeItems" &&
                                key !== "isMapDone" &&
                                key !== "treatyClaims"
                            )
                            .filter(
                              ([, value]) =>
                                value !== null &&
                                value !== undefined &&
                                value !== "" &&
                                value !== "N/A" &&
                                value !== false
                            );

                          const sortedEntries = sortByFieldOrder(filteredEntries, fieldOrder);

                          return (
                            <>
                              {sortedEntries.map(([key, value]) => (
                                <div
                                  key={key}
                                  style={{
                                    // marginBottom: "6px",
                                    padding: "12px 0",
                                    display: "flex",
                                    flexDirection: "row",

                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    borderBottom: "0.5px solid #EEEDED",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontFamily: fontFamily,
                                      fontSize: "14px",
                                      color: "#3B3B3B",
                                      fontWeight: "400",
                                    }}
                                  >
                                    {key === "Name of organization" &&
                                    data?.["W9-Tax_classification_individual_sole_proprietor"] ===
                                      "selected"
                                      ? "Beneficial Owner"
                                      : getStandardizedFieldName(key)}
                                    &nbsp;:&nbsp;
                                  </div>
                                  <div
                                    style={{
                                      fontFamily: fontFamily,
                                      fontSize: "14px",
                                      color: "var(--application-primary-color)",
                                      fontWeight: "400",

                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {typeof value === "object" && value !== null ? (
                                      <div style={{ marginLeft: "10px" }}>
                                        {Object.entries(value)
                                          .filter(
                                            ([k]) =>
                                              k !== "tables" &&
                                              k !== "viewPdf" &&
                                              k !== "formType" &&
                                              k !== "Form number" &&
                                              k !== "Form_number" &&
                                              k !== "Entity Type" &&
                                              k !== "Chapter4Status" &&
                                              k !== "FormType" &&
                                              k !== "Form Type" &&
                                              k !== "incomeItems" &&
                                              k !== "isMapDone" &&
                                              k !== "treatyClaims"
                                          )
                                          .filter(
                                            ([, v]) =>
                                              v !== null &&
                                              v !== undefined &&
                                              v !== "" &&
                                              v !== "N/A" &&
                                              v !== false
                                          )
                                          .map(([nestedKey, nestedValue]) => (
                                            <div key={nestedKey} style={{ marginBottom: "4px" }}>
                                              <span
                                                style={{
                                                  fontSize: "14px",
                                                  color: "#3B3B3B",
                                                  fontWeight: "400",
                                                }}
                                              >
                                                {getStandardizedFieldName(nestedKey)}:
                                              </span>
                                              <span
                                                style={{
                                                  marginLeft: "8px",
                                                  fontSize: "14px",
                                                  color: "var(--application-primary-color)",
                                                  fontWeight: "400",
                                                }}
                                              >
                                                {typeof nestedValue === "object" &&
                                                nestedValue !== null
                                                  ? JSON.stringify(nestedValue)
                                                  : nestedValue}
                                              </span>
                                            </div>
                                          ))}
                                      </div>
                                    ) : typeof value === "object" ? (
                                      JSON.stringify(value)
                                    ) : value === true ? (
                                      "selected"
                                    ) : (
                                      value
                                    )}
                                  </div>
                                </div>
                              ))}
                              <FormTablesDisplay data={data} fontFamily={fontFamily} />
                            </>
                          );
                        }

                        return (
                          <div
                            style={{
                              padding: "8px",
                              backgroundColor: "#f5f5f5",
                              borderRadius: "4px",
                            }}
                          >
                            {String(data)}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div
                      style={{
                        color: "#666",
                        fontStyle: "italic",
                        fontFamily: fontFamily,
                        fontSize: "12px",
                      }}
                    >
                      No extracted data available
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Middle – Matching Form Data */}
            {singleFormMatchingData && singleFormMatchingData.matchCount > 0 && (
              <div
                style={{
                  width:
                    singleFormData && Object.keys(singleFormData).length > 0 ? "33.33%" : "50%",
                  padding: "15px",
                  overflowY: "auto",
                  borderRight: "1px solid #e0e0e0",
                  backgroundColor: "#fafafa",
                }}
              >
                <h6
                  style={{
                    color: "var(--application-primary-color)",
                    fontFamily: fontFamily,
                    marginBottom: "15px",
                    fontSize: "14px",
                  }}
                >
                  Matching Form Data from Admin ({singleFormMatchingData.matchCount} found)
                </h6>
                <div style={{ height: "calc(100% - 80px)", overflow: "auto" }}>
                  {singleFormMatchingData.entities.map((entity, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #28a745",
                        borderRadius: "8px",
                        padding: "12px",
                        marginBottom: "12px",
                        backgroundColor: "#f8fff8",
                      }}
                    >
                      <h6
                        style={{
                          color: "#28a745",
                          fontFamily: fontFamily,
                          marginBottom: "10px",
                          fontSize: "13px",
                        }}
                      >
                        Match {index + 1}
                      </h6>
                      <div style={{ marginBottom: "8px" }}>
                        {Object.entries(entity).map(
                          ([key, value]) =>
                            value && (
                              <div key={key} style={{ marginBottom: "8px" }}>
                                <div
                                  style={{
                                    color: "#666",
                                    fontFamily: fontFamily,
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {getStandardizedFieldName(key)}&nbsp;:
                                </div>
                                <div
                                  style={{
                                    color: "#28a745",
                                    fontFamily: fontFamily,
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {(() => {
                                    const renderValue = (val, depth = 0) => {
                                      let parsedValue = val;
                                      if (
                                        typeof val === "string" &&
                                        (val.startsWith("{") || val.startsWith("["))
                                      ) {
                                        try {
                                          parsedValue = JSON.parse(val);
                                        } catch (e) {
                                          parsedValue = val;
                                        }
                                      }
                                      if (
                                        typeof parsedValue === "object" &&
                                        parsedValue !== null &&
                                        !Array.isArray(parsedValue)
                                      ) {
                                        return (
                                          <div style={{ marginLeft: depth > 0 ? "10px" : "0px" }}>
                                            {Object.entries(parsedValue).map(
                                              ([nestedKey, nestedValue]) =>
                                                nestedValue && (
                                                  <div
                                                    key={nestedKey}
                                                    style={{ marginBottom: "4px" }}
                                                  >
                                                    <span
                                                      style={{
                                                        fontSize: "12px",
                                                        color: "#666",
                                                        fontWeight: "500",
                                                      }}
                                                    >
                                                      {getStandardizedFieldName(nestedKey)}:
                                                    </span>
                                                    {typeof nestedValue === "object" &&
                                                    nestedValue !== null ? (
                                                      renderValue(nestedValue, depth + 1)
                                                    ) : (
                                                      <span
                                                        style={{
                                                          marginLeft: "8px",
                                                          fontSize: "14px",
                                                          color: "#28a745",
                                                          fontWeight: "bold",
                                                        }}
                                                      >
                                                        {String(nestedValue) || "N/A"}
                                                      </span>
                                                    )}
                                                  </div>
                                                )
                                            )}
                                          </div>
                                        );
                                      } else {
                                        return String(parsedValue);
                                      }
                                    };
                                    return renderValue(value);
                                  })()}
                                </div>
                              </div>
                            )
                        )}
                      </div>
                      {/* Action buttons */}
                      <div
                        style={{
                          marginTop: "12px",
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          onClick={() => handleAssignMatchingIdentifier(entity)}
                          style={{
                            backgroundColor: "#28a745",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding: "8px 16px",
                            fontSize: "12px",
                            fontWeight: "500",
                            cursor: "pointer",
                            fontFamily: fontFamily,
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = "#218838";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = "#28a745";
                          }}
                        >
                          Assign Entity ID:{" "}
                          {entity.entity?.uniqueIdentifier || entity.uniqueIdentifier || "N/A"}
                        </button>
                        <button
                          onClick={() => {
                            const formId = entity.form?.formId;
                            if (formId && selectedCheckbox) {
                              setSelectedFormIdForComparison(formId);
                              setCurrentPdfFileImportId(selectedCheckbox);
                              setIsFormComparisonOpen(true);
                            }
                          }}
                          className="single-form-view-btn"
                        >
                          View Form
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Right side – PDF Viewer */}
            <div
              style={{
                width:
                  singleFormMatchingData && singleFormMatchingData.matchCount > 0
                    ? singleFormData && Object.keys(singleFormData).length > 0
                      ? "33.33%"
                      : "50%"
                    : "50%",
                padding: "15px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  flex: 1,
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                {selectedCheckbox ? (
                  <SecureDocumentViewer
                    organizationId={singleFormData?.row?.organizationId}
                    userId={singleFormData?.row?.userID}
                    fileImportId={selectedCheckbox}
                    fileName={singleFormData?.row?.fileName}
                    style={{ height: "100%", width: "100%" }}
                    fileType="uploadfiles"
                    businessunit={
                      singleFormData?.row?.filePath
                        ? singleFormData?.row?.businessUnit || undefined
                        : undefined
                    }
                    filePath={singleFormData?.row?.filePath || undefined}
                    useFastLoad
                  />
                ) : (
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#666",
                      fontStyle: "italic",
                      fontFamily: fontFamily,
                      fontSize: "12px",
                    }}
                  >
                    PDF not available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Form Comparison Modal */}
      <FormComparisonModal
        open={isFormComparisonOpen}
        onClose={onCloseComparison}
        existingPdfFileImportId={currentPdfFileImportId}
        existingPdfFileName={singleFormData?.row?.fileName || ""}
        formId={selectedFormIdForComparison}
        existingPdfTitle="Extracted Form"
        adminFormTitle="Admin Form"
        userId={singleFormData?.row?.userID}
        organizationId={singleFormData?.row?.organizationId}
        businessUnitId={singleFormData?.row?.businessUnitId}
      />
    </>
  );
}

export default SingleFormPopup;
